import React, { useRef } from "react";
import { gsap, useGSAP, prefersReducedMotion } from "../../lib/gsap";
import { personalInfo } from "../../data/portfolio";
import { scrollToSection } from "../../lib/scrollTo";
import CopyEmail from "../ui/CopyEmail";
import Magnetic from "../ui/Magnetic";
import Icon from "../ui/Icon";
import Decode from "../motion/Decode";
import Ruler from "../motion/Ruler";
import FieldCanvas from "../bg/FieldCanvas";
import Guides from "../bg/Guides";

const [local, domain] = personalInfo.email.split("@");

const CHANNELS = [
  { label: "GitHub", value: "Wayn-Git", href: personalInfo.github },
  { label: "LinkedIn", value: "bilal-rukundi", href: personalInfo.linkedin },
  { label: "Résumé", value: "PDF", href: personalInfo.cvUrl },
];

/**
 * The ending. Revealed from under the sheet, so its own motion is a slow rise
 * scrubbed against the reveal distance — the two read as one movement.
 */
const Contact = () => {
  const scope = useRef(null);

  useGSAP(
    () => {
      if (prefersReducedMotion()) return undefined;
      const spacer = document.querySelector("[data-reveal-spacer]");
      if (!spacer) return undefined;

      const tween = gsap.fromTo(
        scope.current.querySelectorAll("[data-rise]"),
        { y: 56, opacity: 0.35 },
        {
          y: 0,
          opacity: 1,
          ease: "none",
          stagger: 0.05,
          scrollTrigger: {
            trigger: spacer,
            start: "top bottom",
            end: "top 20%",
            scrub: 0.5,
          },
        }
      );

      return () => tween.scrollTrigger?.kill();
    },
    { scope }
  );

  /* The panel is uncovered by scroll, so keyboard focus landing inside it has
     to bring the page with it — otherwise focus sits on something hidden. */
  const revealOnFocus = () => {
    const max = document.documentElement.scrollHeight - window.innerHeight;
    if (window.scrollY < max - 8) scrollToSection("contact");
  };

  return (
    <section
      ref={scope}
      onFocus={revealOnFocus}
      aria-labelledby="contact-title"
      className="chapter chapter-dark px-edge flex h-full flex-col justify-between overflow-hidden pb-20 pt-20 md:pb-10 md:pt-24"
    >
      {/* The closing chapter is exactly one viewport, so it needs no sticking */}
      <FieldCanvas className="field-inset" />
      <Guides className="field-inset" />

      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h2 id="contact-title" className="t-label">
          <span className="text-flare">06</span> — Transmission
          <span className="sr-only"> — contact</span>
        </h2>
        <span className="t-label">Find me here</span>
      </div>

      <div data-rise className="py-4">
        <p className="t-serif text-dim" style={{ fontSize: "clamp(1.5rem, 3vw, 2.75rem)", lineHeight: 1.1 }}>
          Say hello.
        </p>

        <a
          href={`mailto:${personalInfo.email}`}
          className="group block"
          data-cursor-label="Mail"
          aria-label={`Email ${personalInfo.email}`}
        >
          <span
            className="mt-3 block text-fg transition-[font-variation-settings] duration-500 ease-out group-hover:[font-variation-settings:'wdth'_118,'wght'_760]"
            style={{
              fontSize: "clamp(1.5rem, 5.6vw, 5.25rem)",
              lineHeight: 0.92,
              letterSpacing: "-0.04em",
              fontVariationSettings: "'wdth' 108, 'wght' 700",
            }}
          >
            <span className="block">{local}</span>
            <span className="block">
              <span className="text-flare">@</span>
              {domain}
            </span>
          </span>
        </a>

        <div data-rise className="mt-8">
          <CopyEmail />
        </div>
      </div>

      <div data-rise>
        <Ruler ticks={20} className="mb-6" />
        <div className="grid gap-x-8 gap-y-6 md:grid-cols-4">
          {CHANNELS.map((channel) => (
            <a
              key={channel.label}
              href={channel.href}
              target="_blank"
              rel="noreferrer noopener"
              className="group flex items-baseline justify-between gap-4 md:flex-col md:items-start md:gap-1.5"
              data-cursor-label="Open"
            >
              <span className="t-label">{channel.label}</span>
              <span className="link-wipe flex items-center gap-1.5 text-sm text-fg">
                {channel.value}
                <Icon name="arrowOut" />
              </span>
            </a>
          ))}

          <div className="flex items-baseline justify-between gap-4 md:flex-col md:items-start md:gap-1.5">
            <span className="t-label">Based in</span>
            <span className="text-sm text-fg">{personalInfo.location}</span>
          </div>
        </div>

        <div className="hair-t mt-6 flex flex-wrap items-center justify-between gap-4 pt-4">
          <span className="t-label">
            {personalInfo.fullName} — {personalInfo.title}
          </span>
          <Magnetic padding={14}>
            <button
              type="button"
              onClick={() => scrollToSection("top")}
              className="t-label-strong flex min-h-[44px] items-center gap-2 px-1"
              data-cursor-label="Top"
            >
              Back to top
              <Icon name="arrowDown" className="rotate-180" />
            </button>
          </Magnetic>
        </div>
      </div>
    </section>
  );
};

export default Contact;
